import { Phone, Mail, Target, Eye, Award, Users } from "lucide-react";
import { directors, stats, company } from "../mock";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { products } from "../mock";

const values = [
  { icon: Target, title: "Our Mission", text: "To deliver reliable, well-engineered window control and natural ventilation solutions, installed to the highest standard and backed by genuine expertise." },
  { icon: Eye, title: "Our Approach", text: "We listen first, specify the right products for every application, and work within budget without ever compromising on quality or safety." },
  { icon: Award, title: "Our Promise", text: "On time, on budget and done properly — from survey and supply through to installation, commissioning and aftercare." },
];

const About = () => {
  return (
    <div>
      <PageHero
        eyebrow="About L&D Controls"
        title="About Us"
        subtitle="L&D Controls Ltd specialises in the supply and installation of manual and electric window control systems and natural ventilation, serving residential, commercial, education and healthcare clients nationwide."
      />

      {/* Story */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
          <div className="img-zoom overflow-hidden rounded-2xl shadow-xl">
            <img src={products[4].image} alt="Window control project" className="h-[420px] w-full object-cover" />
          </div>
          <div>
            <p className="eyebrow text-[#1d4ed8]">Our Story</p>
            <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">
              Experience you can build on
            </h2>
            <div className="ld-accent-bar mt-5" />
            <p className="mt-6 text-base leading-relaxed text-gray-600">
              Founded on decades of combined, hands-on experience in window controls, L&amp;D Controls
              brings together everything from traditional manual Teleflex winder systems to modern
              electric actuators and fully automated natural ventilation.
            </p>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              We understand that every window and every building is different. That first-hand product
              knowledge means we can recommend and install the right solution the first time — whether
              it is a single opener in a home or hundreds of units across a large project.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {stats.slice(0, 2).map((s) => (
                <div key={s.label}>
                  <p className="font-head text-3xl font-800 text-[#1d4ed8]">{s.value}</p>
                  <p className="mt-1 text-sm text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="card-hover rounded-xl border border-gray-100 bg-white p-8">
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-blue-50 text-[#1d4ed8]">
                  <v.icon size={26} />
                </div>
                <h3 className="mt-5 font-head text-lg font-700 text-[#0f2a5a]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Directors */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="mb-12 text-center">
            <p className="eyebrow text-[#1d4ed8]">Meet The Team</p>
            <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">Our Directors</h2>
            <div className="ld-accent-bar mx-auto mt-5" />
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {directors.map((d) => (
              <div key={d.email} className="rounded-xl border border-gray-100 bg-gray-50 p-8">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0f2a5a] text-xl font-800 text-white font-head">
                    {d.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <h3 className="font-head text-xl font-700 text-[#0f2a5a]">{d.name}</h3>
                    <p className="text-sm font-semibold uppercase tracking-wider text-[#1d4ed8]">{d.role}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-gray-600">{d.bio}</p>
                <div className="mt-5 flex flex-wrap gap-4 border-t border-gray-200 pt-4 text-sm">
                  <a href={`tel:${d.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-gray-700 hover:text-[#1d4ed8]">
                    <Phone size={15} className="text-[#1d4ed8]" /> {d.phone}
                  </a>
                  <a href={`mailto:${d.email}`} className="flex items-center gap-2 text-gray-700 hover:text-[#1d4ed8]">
                    <Mail size={15} className="text-[#1d4ed8]" /> {d.email}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-gray-400">
            {company.name} &middot; Company No. {company.companyNumber}
          </p>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default About;
